import { useState } from 'react';
import type { AclRuleDetail } from '#server/models';
import { useAreaAcl } from '../hooks';
import { ColoredBadge } from '../components';
import { ADMIN_SCROLLBAR_CLASSES, ACCESS_TYPE_COLORS, ACCESS_TYPE_SORT_ORDER } from '../constants';
import { AclUsersPanel } from './AclUsersPanel';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface AreaAclProps {
  areaSlug: string;
}

export const AreaAcl = ({ areaSlug }: AreaAclProps) => {
  const { data: acl, isLoading, isError, error } = useAreaAcl(areaSlug);
  const [selectedRule, setSelectedRule] = useState<AclRuleDetail | null>(null);

  const sortedRules = acl
    ? [...acl.rules].sort((a, b) => (ACCESS_TYPE_SORT_ORDER[a.accessType] ?? 99) - (ACCESS_TYPE_SORT_ORDER[b.accessType] ?? 99))
    : [];

  return (
    <div className="flex gap-6 px-4 py-6" style={{ height: 'calc(100vh - 56px)' }}>

      {/* Left: rules list */}
      <div className="flex-1 min-w-0 flex flex-col min-h-0">
        <div className="flex-shrink-0 pb-3 border-b border-[#2D2D2D]">
          <h1 className="text-xl font-bold text-[#E8E8E8]">Access Control List</h1>
          {acl && (
            <p className="text-sm text-[#999] mt-1">
              {acl.name}{acl.description ? ` — ${acl.description}` : ''}
              {' · '}{acl.rules.length} {acl.rules.length === 1 ? 'rule' : 'rules'}
            </p>
          )}
        </div>

        <div className="flex-1 min-h-0 mt-4">
          {isLoading && <div className="text-sm text-[#999]">Loading…</div>}

          {isError && (
            <div className="text-sm text-[#999]">
              {(error as any)?.data?.code === 'FORBIDDEN'
                ? 'You need admin access to view the access control list for this area.'
                : 'Failed to load access control list.'}
            </div>
          )}

          {acl && acl.rules.length === 0 && (
            <div className="text-sm text-[#999]">No rules configured for this ACL.</div>
          )}

          {acl && acl.rules.length > 0 && (
            <div className={`h-full overflow-y-auto ${ADMIN_SCROLLBAR_CLASSES} space-y-3 pr-2`}>
              {sortedRules.map((rule) => (
                <RuleCard key={rule.id} rule={rule} onDetails={() => setSelectedRule(rule)} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Divider */}
      <div className="w-px bg-[#2D2D2D] shrink-0" />

      {/* Right: users panel */}
      <div className="w-[35%] shrink-0 min-h-0">
        {acl
          ? <AclUsersPanel areaSlug={areaSlug} rules={acl.rules} />
          : <div className="text-sm text-[#999]">{isLoading ? 'Loading…' : ''}</div>
        }
      </div>

      {selectedRule && (
        <RuleDetailDialog rule={selectedRule} onClose={() => setSelectedRule(null)} />
      )}
    </div>
  );
};

const RuleCard = ({ rule, onDetails }: { rule: AclRuleDetail; onDetails: () => void }) => {
  const color = ACCESS_TYPE_COLORS[rule.accessType] ?? '#888';
  const hasConstraints = rule.groups.length > 0 || rule.units.length > 0;

  return (
    <div className="rounded border border-[#2D2D2D] bg-[#222] p-4 space-y-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <span className="text-sm font-medium text-[#E8E8E8]">
          {rule.name ?? 'Unnamed rule'}
        </span>
        <ColoredBadge color={color} bold>{rule.accessType}</ColoredBadge>
      </div>

      {rule.description && (
        <p className="text-xs text-[#999]">{rule.description}</p>
      )}

      {/* User type */}
      <div className="flex gap-3 text-sm">
        <span className="text-[#999] shrink-0 w-36">User type</span>
        <span className="text-[#E8E8E8] capitalize">
          {rule.userType === 'ANY' ? 'Any' : rule.userType.toLowerCase()}
        </span>
      </div>

      {/* No constraints */}
      {!hasConstraints && (
        <p className="text-xs text-[#666] italic">
          No group or unit restrictions — auto-grants {rule.accessType} to any matching user.
        </p>
      )}

      {/* Groups (AND) */}
      {rule.groups.length > 0 && (
        <div className="space-y-2">
          <span className="text-xs font-semibold text-[#999] uppercase tracking-wide">
            Groups — must belong to ALL
          </span>
          <div className="flex flex-wrap items-center gap-1.5">
            {rule.groups.map((group, i) => (
              <span key={group.groupId} className="flex items-center gap-1.5">
                {i > 0 && (
                  <span className="text-xs font-bold text-[#555]">AND</span>
                )}
                <span className="text-xs px-2 py-0.5 rounded-full bg-[#2A2A2A] border border-[#3D3D3D] text-[#CCC]">
                  {group.name}
                  {group.isSuperAdmin && ' ★'}
                </span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* AND connector between groups and units */}
      {rule.groups.length > 0 && rule.units.length > 0 && (
        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-[#2D2D2D]" />
          <span className="text-xs font-bold text-[#555]">AND</span>
          <div className="flex-1 h-px bg-[#2D2D2D]" />
        </div>
      )}

      {/* Units (OR) */}
      {rule.units.length > 0 && (
        <div className="space-y-2">
          <span className="text-xs font-semibold text-[#999] uppercase tracking-wide">
            Unit enrolments — at least ONE required
          </span>
          <div className="flex flex-wrap items-center gap-1.5">
            {rule.units.map((unit, i) => (
              <span key={unit.unitId} className="flex items-center gap-1.5">
                {i > 0 && (
                  <span className="text-xs font-bold text-[#555]">OR</span>
                )}
                <span className="text-xs px-2 py-0.5 rounded-full bg-[#2A2A2A] border border-[#3D3D3D] font-mono text-[#CCC]">
                  {unit.udc}
                </span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex justify-end pt-1 border-t border-[#2D2D2D]">
        <button
          onClick={onDetails}
          className="text-xs text-[#C3D82E] hover:underline mt-2"
        >
          Details →
        </button>
      </div>
    </div>
  );
};

const RuleDetailDialog = ({ rule, onClose }: { rule: AclRuleDetail; onClose: () => void }) => {
  const color = ACCESS_TYPE_COLORS[rule.accessType] ?? '#888';

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className={`max-w-2xl border-[#333] max-h-[80vh] overflow-y-auto ${ADMIN_SCROLLBAR_CLASSES}`}
        style={{ backgroundColor: '#1C1C1C', color: '#E8E8E8' }}
      >
        <DialogHeader>
          <DialogTitle className="text-[#E8E8E8]">
            {rule.name ?? 'Unnamed rule'}
          </DialogTitle>
          {rule.description && (
            <p className="text-sm text-[#999]">{rule.description}</p>
          )}
        </DialogHeader>

        <div className="space-y-5">
          {/* Meta */}
          <dl className="space-y-2 text-sm">
            <div className="flex gap-4 items-center">
              <dt className="text-[#999] w-28 shrink-0">Access type</dt>
              <dd><ColoredBadge color={color} bold>{rule.accessType}</ColoredBadge></dd>
            </div>
            <div className="flex gap-4">
              <dt className="text-[#999] w-28 shrink-0">User type</dt>
              <dd className="text-[#E8E8E8] capitalize">
                {rule.userType === 'ANY' ? 'Any' : rule.userType.toLowerCase()}
              </dd>
            </div>
          </dl>

          {/* Groups */}
          {rule.groups.length > 0 && (
            <section className="space-y-2">
              <h3 className="text-xs font-semibold text-[#C3D82E] uppercase tracking-wide">
                Groups — must belong to ALL
              </h3>
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="text-left border-b border-[#333]">
                    <th className="pb-2 pr-3 font-semibold text-[#999]">Name</th>
                    <th className="pb-2 pr-3 font-semibold text-[#999]">Slug</th>
                    <th className="pb-2 font-semibold text-[#999]">Custom error</th>
                  </tr>
                </thead>
                <tbody>
                  {rule.groups.map((group) => (
                    <tr key={group.groupId} className="border-b border-[#2A2A2A]">
                      <td className="py-2 pr-3 text-[#E8E8E8] font-medium">{group.name}</td>
                      <td className="py-2 pr-3 font-mono text-[#999]">{group.slug}</td>
                      <td className="py-2 text-[#999]">
                        {group.errorTitle ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Custom error details */}
              {rule.groups.some((g) => g.errorMessage) && (
                <div className="space-y-1 pt-1">
                  {rule.groups.filter((g) => g.errorMessage).map((group) => (
                    <div key={group.groupId} className="text-xs text-[#777]">
                      <span className="text-[#999]">{group.name}:</span>{' '}
                      {group.errorMessage}
                      {group.errorLink && (
                        <span>
                          {' — '}
                          <a
                            href={group.errorLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#C3D82E] underline"
                          >
                            {group.errorLinkText ?? group.errorLink}
                          </a>
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Units */}
          {rule.units.length > 0 && (
            <section className="space-y-2">
              <h3 className="text-xs font-semibold text-[#C3D82E] uppercase tracking-wide">
                Unit enrolments — at least ONE required
              </h3>
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="text-left border-b border-[#333]">
                    <th className="pb-2 pr-3 font-semibold text-[#999]">UDC</th>
                    <th className="pb-2 pr-3 font-semibold text-[#999]">Name</th>
                    <th className="pb-2 font-semibold text-[#999]">Post-enrolment days</th>
                  </tr>
                </thead>
                <tbody>
                  {rule.units.map((unit) => (
                    <tr key={unit.unitId} className="border-b border-[#2A2A2A]">
                      <td className="py-2 pr-3 font-mono text-[#C3D82E]">{unit.udc}</td>
                      <td className="py-2 pr-3 text-[#E8E8E8]">{unit.name}</td>
                      <td className="py-2 text-[#999]">
                        {unit.postEnrolledDays ? `${unit.postEnrolledDays} days` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {rule.groups.length === 0 && rule.units.length === 0 && (
            <p className="text-sm text-[#999] italic">
              No group or unit restrictions — auto-grants {rule.accessType} to any matching user.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
